import { useState } from "react";
import { ExternalLink, PackageSearch, Phone, Mail } from "lucide-react";
import { Reveal } from "../components/motion-primitives";
import { CONTACT } from "../data/content";

export default function Tracking() {
  const [number, setNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!number.trim()) return;
    setLoading(true);
    try {
      await fetch("/api/track-requests", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tracking_number: number.trim() }),
      });
      setDone(true);
      setNumber("");
    } catch (err) {
      setDone(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="bg-section py-20 text-center md:py-24" data-testid="tracking-hero">
        <div className="container-x">
          <Reveal>
            <span className="eyebrow-pill">Track &amp; Trace</span>
            <h1 className="mt-5 font-heading text-4xl font-extrabold tracking-tight md:text-5xl">
              Track Your Shipment
            </h1>
            <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-muted-foreground">
              Enter your tracking or AWB number and our team will get you the
              latest status of your consignment.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16 md:py-24" data-testid="tracking-form-section">
        <div className="container-x grid max-w-5xl gap-6 lg:grid-cols-2">
          <Reveal>
            <div className="h-full rounded-2xl border border-border bg-white p-8 shadow-sm md:p-10">
              {done ? (
                <div className="text-center" data-testid="tracking-success">
                  <span className="icon-chip-soft mx-auto" style={{ backgroundColor: "#FEF0DC" }}>
                    <PackageSearch className="h-5 w-5" />
                  </span>
                  <h2 className="mt-5 font-heading text-2xl font-bold">Enquiry received</h2>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    Thank you — our operations team will share the current
                    status of your shipment shortly.
                  </p>
                  <button
                    data-testid="tracking-reset-button"
                    onClick={() => setDone(false)}
                    className="btn-outline mt-7"
                  >
                    Track another shipment
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="tracking-form">
                  <h2 className="font-heading text-2xl font-extrabold tracking-tight">
                    Request a Status Update
                  </h2>
                  <p className="mt-2 text-sm text-muted-foreground">
                    Enter your tracking or AWB number and our operations team
                    will share the latest status.
                  </p>
                  <label htmlFor="tracking-number" className="field-label mt-6">
                    Tracking / AWB Number
                  </label>
                  <input
                    id="tracking-number"
                    data-testid="tracking-number-input"
                    value={number}
                    onChange={(e) => setNumber(e.target.value)}
                    placeholder="e.g. SRF-2024-001234"
                    className="field-input"
                    required
                  />
                  <button
                    type="submit"
                    data-testid="tracking-submit-button"
                    disabled={loading}
                    className="btn-primary mt-6 w-full disabled:opacity-60"
                  >
                    {loading ? "Checking…" : "Track Shipment"}
                  </button>
                </form>
              )}
            </div>
          </Reveal>

          <div className="flex flex-col gap-6">
            <Reveal delay={0.08}>
              <div
                className="rounded-2xl border border-border bg-white p-8 shadow-sm"
                data-testid="aaiclas-card"
              >
                <span className="icon-chip-soft" style={{ backgroundColor: "#FEF0DC" }}>
                  <ExternalLink className="h-5 w-5" />
                </span>
                <h2 className="mt-4 font-heading text-2xl font-extrabold tracking-tight">
                  Export Cargo Tracking
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  Track your export cargo live on the official AAI Cargo
                  Logistics (AAICLAS) portal — real-time status straight from
                  the cargo terminal.
                </p>
                <a
                  href="https://www.aaiclas-ecom.org/live/ExportCargoTracking.aspx"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="aaiclas-link"
                  className="btn-primary mt-6"
                >
                  Track on AAICLAS Portal
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.14}>
              <div className="rounded-2xl border border-border bg-section p-6 text-sm text-muted-foreground">
                <p className="font-semibold text-foreground">Need instant status?</p>
                <p className="mt-2 flex items-center gap-2">
                  <Phone className="h-4 w-4 text-brand" /> {CONTACT.phone}
                </p>
                <p className="mt-2 flex items-center gap-2">
                  <Mail className="h-4 w-4 text-brand" /> {CONTACT.email}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
